/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Spinner,
  SimpleGrid,
  Card,
  CardBody,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Grid,
  Button,
  Tabs,
  TabList,
  Tab,
} from '@chakra-ui/react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import moment from 'moment';
import 'moment/locale/pt-br';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  ChartLineUp,
  Package,
  FileText,
  ShoppingCart,
  File,
  Buildings,
  CalendarBlank,
  TrendUp,
  WarningCircle,
} from 'phosphor-react';
import { ChevronLeft, ChevronRight, Calendar, Maximize2, Minimize2 } from 'lucide-react';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { PageBox } from '../../../../components/pageBox';
import { FormBox } from '../../../../components/formBox';
import {
  useDashboardImportacao,
  type IImportacaoPorDia,
  type IEmpresaPorDia,
  type IResumoImportacoes,
  type IEmpresaPaginada,
  type TipoArquivo,
} from '../../../../hooks/useDashboardImportacao';
import { formatCNPJ } from '../../../../utils/FormatFunctions';

moment.locale('pt-br');

// Cores para os tipos de arquivos
const COLORS = {
  NFe: '#22C55E',
  NFSe: '#8B5CF6',
  NFCe: '#F97316',
  CTe: '#3B82F6',
};

// eslint-disable-next-line no-unused-vars
type SelectMonthHandler = (year: number, month: number) => void;

interface MonthNavigatorProps {
  currentDate: moment.Moment;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectMonth: SelectMonthHandler;
}

function MonthNavigator({
  currentDate,
  onPrevMonth,
  onNextMonth,
  onSelectMonth,
}: MonthNavigatorProps) {
  const monthYear = currentDate.format('MMMM [de] YYYY');
  const isCurrentMonth = currentDate.isSame(moment(), 'month');
  const [selectedYear, setSelectedYear] = useState(currentDate.year());

  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];

  const currentYear = moment().year();
  const currentMonth = moment().month();

  const handleYearChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setSelectedYear(prev => prev - 1);
    } else if (direction === 'next' && selectedYear < currentYear) {
      setSelectedYear(prev => prev + 1);
    }
  };

  const isMonthDisabled = (monthIndex: number) => {
    return selectedYear === currentYear && monthIndex > currentMonth;
  };

  const isMonthSelected = (monthIndex: number) => {
    return selectedYear === currentDate.year() && monthIndex === currentDate.month();
  };

  return (
    <HStack
      spacing={{ base: 1, md: 2 }}
      bg="white"
      px={{ base: 2, md: 4 }}
      py={2}
      borderRadius="lg"
      shadow="sm"
      w={{ base: 'full', md: 'auto' }}
      justify="center"
    >
      <IconButton
        aria-label="Mês anterior"
        icon={<Icon as={ChevronLeft} />}
        onClick={onPrevMonth}
        variant="ghost"
        size={{ base: 'sm', md: 'sm' }}
        colorScheme="blue"
      />
      <Popover placement="bottom">
        <PopoverTrigger>
          <HStack
            spacing={{ base: 1, md: 2 }}
            minW={{ base: '140px', md: '180px' }}
            justify="center"
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
            px={2}
            py={1}
            borderRadius="md"
          >
            <Icon as={Calendar} color="gray.500" boxSize={{ base: 3, md: 4 }} />
            <Text
              fontWeight="medium"
              fontSize={{ base: 'xs', md: 'sm' }}
              textTransform="capitalize"
              color="gray.700"
            >
              {monthYear}
            </Text>
          </HStack>
        </PopoverTrigger>
        <PopoverContent width={{ base: '260px', md: '280px' }}>
          <PopoverBody p={{ base: 3, md: 4 }}>
            {/* Seletor de Ano */}
            <HStack justify="space-between" mb={4}>
              <IconButton
                aria-label="Ano anterior"
                icon={<Icon as={ChevronLeft} boxSize={4} />}
                onClick={() => handleYearChange('prev')}
                variant="ghost"
                size="sm"
              />
              <Text fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>
                {selectedYear}
              </Text>
              <IconButton
                aria-label="Próximo ano"
                icon={<Icon as={ChevronRight} boxSize={4} />}
                onClick={() => handleYearChange('next')}
                variant="ghost"
                size="sm"
                isDisabled={selectedYear >= currentYear}
              />
            </HStack>
            {/* Grid de Meses */}
            <Grid templateColumns="repeat(4, 1fr)" gap={{ base: 1, md: 2 }}>
              {months.map((month, index) => (
                <Button
                  key={month}
                  size="sm"
                  variant={isMonthSelected(index) ? 'solid' : 'ghost'}
                  colorScheme={isMonthSelected(index) ? 'blue' : 'gray'}
                  isDisabled={isMonthDisabled(index)}
                  onClick={() => onSelectMonth(selectedYear, index)}
                  fontSize="xs"
                >
                  {month}
                </Button>
              ))}
            </Grid>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      <IconButton
        aria-label="Próximo mês"
        icon={<Icon as={ChevronRight} />}
        onClick={onNextMonth}
        variant="ghost"
        size={{ base: 'sm', md: 'sm' }}
        colorScheme="blue"
        isDisabled={isCurrentMonth}
      />
    </HStack>
  );
}

export function ModulesHomeIntegrationImportsDashboardPage() {
  const {
    getImportacoesPorDia,
    getEmpresasPorDia,
    getResumoImportacoes,
    getEmpresasPorTipoInfinite,
  } = useDashboardImportacao();
  // Filtros de data usando URLSearchParams
  const [filters, setFilters] = useSearchParams(
    createSearchParams({
      date_start: moment().startOf('month').format('YYYY-MM-DD'),
      date_end: moment().endOf('month').format('YYYY-MM-DD'),
    }),
  );

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTipoArquivo, setSelectedTipoArquivo] = useState<TipoArquivo>('NFe');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  // Refs para infinite scroll e fullscreen
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);

  // Extrair dateStart e dateEnd dos filtros e garantir que seja mês fechado
  const { dateStart, dateEnd, currentFilterDate } = useMemo(() => {
    const rawDateStart = filters.get('date_start');
    const baseMoment = rawDateStart ? moment(rawDateStart) : moment();

    return {
      dateStart: baseMoment.clone().startOf('month').format('YYYY-MM-DD'),
      dateEnd: baseMoment.clone().endOf('month').format('YYYY-MM-DD'),
      currentFilterDate: baseMoment.clone().startOf('month'),
    };
  }, [filters]);

  // Funções para navegar entre meses
  const handlePrevMonth = () => {
    const prevMonth = currentFilterDate.clone().subtract(1, 'month');
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: prevMonth.startOf('month').format('YYYY-MM-DD'),
        date_end: prevMonth.endOf('month').format('YYYY-MM-DD'),
      }),
    );
  };

  const handleNextMonth = () => {
    const nextMonth = currentFilterDate.clone().add(1, 'month');
    if (!nextMonth.isAfter(moment(), 'month')) {
      setFilters(
        createSearchParams({
          ...Object.fromEntries(filters),
          date_start: nextMonth.startOf('month').format('YYYY-MM-DD'),
          date_end: nextMonth.endOf('month').format('YYYY-MM-DD'),
        }),
      );
    }
  };

  const handleSelectMonth = (year: number, month: number) => {
    const newSelectedDate = moment().year(year).month(month);
    setFilters(
      createSearchParams({
        ...Object.fromEntries(filters),
        date_start: newSelectedDate.startOf('month').format('YYYY-MM-DD'),
        date_end: newSelectedDate.endOf('month').format('YYYY-MM-DD'),
      }),
    );
  };

  // Query para resumo
  const { data: resumoData, isFetching: isFetchingResumo } = useQuery({
    queryKey: ['dashboardImportacao-resumo', { date_start: dateStart, date_end: dateEnd }],
    queryFn: getResumoImportacoes,
  });

  // Query para importações por dia
  const { data: importacoesPorDiaData, isFetching: isFetchingPorDia } = useQuery({
    queryKey: ['dashboardImportacao-porDia', { date_start: dateStart, date_end: dateEnd }],
    queryFn: getImportacoesPorDia,
  });

  // Infinite query para empresas por tipo
  const {
    data: empresasInfiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingEmpresas,
  } = useInfiniteQuery({
    queryKey: [
      'dashboardImportacao-empresasInfinite',
      { date_start: dateStart, date_end: dateEnd, tipo_arquivo: selectedTipoArquivo },
    ],
    queryFn: getEmpresasPorTipoInfinite,
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
  });

  // Query para empresas do dia selecionado
  const { data: empresasPorDiaData, isFetching: isFetchingEmpresasDia } = useQuery({
    queryKey: ['dashboardImportacao-empresasDia', { data: selectedDate }],
    queryFn: getEmpresasPorDia,
    enabled: !!selectedDate,
  });

  const resumo: IResumoImportacoes | undefined = resumoData?.data;
  const importacoesPorDia: IImportacaoPorDia[] = importacoesPorDiaData?.data ?? [];
  const empresasDoDia: IEmpresaPorDia[] = empresasPorDiaData?.data ?? [];

  // Empresas sem movimentação = total ativas no período - empresas com importação no período
  const totalAtivasNoPeriodo = resumo?.total_empresas_ativas ?? 0;
  const empresasSemMovimentacao = Math.max(totalAtivasNoPeriodo - (resumo?.total_empresas ?? 0), 0);

  // Flatten das páginas de empresas
  const allEmpresas: IEmpresaPaginada[] = useMemo(() => {
    return empresasInfiniteData?.pages?.flatMap(page => page.items) ?? [];
  }, [empresasInfiniteData]);

  const totalEmpresas = empresasInfiniteData?.pages?.[0]?.total ?? 0;

  // Handler para infinite scroll
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Adicionar listener de scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [handleScroll]);

  // Handler para fullscreen
  const toggleFullscreen = useCallback(async () => {
    if (!dashboardRef.current) return;

    if (!document.fullscreenElement) {
      try {
        await dashboardRef.current.requestFullscreen();
        setIsFullscreen(true);
      } catch {
        // Erro ao entrar em tela cheia - ignorado silenciosamente
      }
    } else {
      try {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } catch {
        // Erro ao sair de tela cheia - ignorado silenciosamente
      }
    }
  }, []);

  // Listener para detectar mudanças no fullscreen (ex: ESC)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handler para mudança de tab
  const handleTabChange = (index: number) => {
    const tipos: TipoArquivo[] = ['NFe', 'NFSe', 'NFCe', 'CTe'];
    setSelectedTipoArquivo(tipos[index]);
  };

  // Formatar dados para o gráfico de linha
  const chartData = useMemo(() => {
    return importacoesPorDia.map(item => ({
      ...item,
      dataFormatada: item.data ? moment(item.data).format('DD/MM') : '',
    }));
  }, [importacoesPorDia]);

  // Handler para clique no gráfico
  const handleChartClick = (data: any) => {
    if (data?.activePayload?.[0]?.payload?.data) {
      setSelectedDate(data.activePayload[0].payload.data);
      onOpen();
    }
  };

  // Card de resumo
  const SummaryCard = ({
    label,
    value,
    icon: IconComponent,
    iconColor,
    bgColor,
  }: {
    label: string;
    value: number | string;
    icon: any;
    iconColor: string;
    bgColor: string;
  }) => (
    <Card bg="white" borderRadius="16px" shadow="sm" h="100%">
      <CardBody p={3}>
        <Flex alignItems="center" gap={2}>
          <Box p={2} borderRadius="lg" bg={bgColor} flexShrink={0}>
            <IconComponent size={18} color={iconColor} weight="duotone" />
          </Box>
          <Box flex={1} minW={0}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium" whiteSpace="nowrap">
              {label}
            </Text>
            <Text
              fontSize={{ base: 'sm', lg: 'md' }}
              fontWeight="bold"
              color="gray.800"
              whiteSpace="nowrap"
            >
              {typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
            </Text>
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );

  // Custom tooltip para o gráfico
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box bg="white" p={3} borderRadius="md" shadow="lg" border="1px" borderColor="gray.200">
          <Text fontWeight="bold" mb={2}>
            {moment(payload[0]?.payload?.data).format('DD [de] MMMM')}
          </Text>
          <Text fontSize="sm" color="blue.600">
            Empresas: <strong>{payload[0]?.value}</strong>
          </Text>
          <Text fontSize="sm" color="green.600">
            Notas: <strong>{payload[1]?.value?.toLocaleString('pt-BR')}</strong>
          </Text>
          <Text fontSize="xs" color="gray.500" mt={2}>
            Clique para ver detalhes
          </Text>
        </Box>
      );
    }
    return null;
  };

  return (
    <PageBox>
      <Box
        ref={dashboardRef}
        px={{ base: 4, md: 6 }}
        py={{ base: 4, md: 6 }}
        bg={isFullscreen ? 'gray.50' : 'transparent'}
        minH={isFullscreen ? '100vh' : 'auto'}
        overflowY={isFullscreen ? 'auto' : 'visible'}
      >
        {/* Header */}
        <Flex
          flexDirection={{ base: 'column', lg: 'row' }}
          alignItems={{ base: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
          gap={4}
          mb={6}
        >
          <Flex alignItems="center" gap={3}>
            {!isFullscreen && <ReturnButton previousPage="modulesHome/integration/dailyImports" />}
            <Flex
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              bg="#E0F2FE"
              p={3}
            >
              <ChartLineUp color="#0077B6" size={32} />
            </Flex>
            <Box>
              <Text fontWeight="bold" fontSize={{ base: '18px', md: '20px' }}>
                Dashboard de Importações
              </Text>
              <Text fontSize="sm" color="gray.500">
                Análise mensal de importações de notas fiscais
              </Text>
            </Box>
          </Flex>

          <HStack spacing={2}>
            {/* Navegador de mês */}
            <MonthNavigator
              currentDate={currentFilterDate}
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onSelectMonth={handleSelectMonth}
            />

            {/* Botão Fullscreen */}
            <IconButton
              aria-label={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              icon={<Icon as={isFullscreen ? Minimize2 : Maximize2} boxSize={5} />}
              onClick={toggleFullscreen}
              variant="ghost"
              colorScheme="blue"
              size="md"
              title={isFullscreen ? 'Sair da tela cheia (ESC)' : 'Maximizar dashboard'}
            />
          </HStack>
        </Flex>

        {/* Cards de resumo */}
        {isFetchingResumo ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="blue.500" />
          </Flex>
        ) : (
          <SimpleGrid
            columns={{ base: 2, sm: 3, md: 4, xl: 9 }}
            spacing={{ base: 2, md: 3 }}
            mb={6}
          >
            <SummaryCard
              label="Total de Notas"
              value={resumo?.total_notas ?? 0}
              icon={TrendUp}
              iconColor="#0077B6"
              bgColor="#E0F2FE"
            />
            <SummaryCard
              label="Empresas"
              value={resumo?.total_empresas ?? 0}
              icon={Buildings}
              iconColor="#6B7280"
              bgColor="#F3F4F6"
            />
            <SummaryCard
              label="S/ Movimentação"
              value={empresasSemMovimentacao}
              icon={WarningCircle}
              iconColor="#F59E0B"
              bgColor="#FEF3C7"
            />
            <SummaryCard
              label="Dias Ativos"
              value={resumo?.dias_com_importacao ?? 0}
              icon={CalendarBlank}
              iconColor="#8B5CF6"
              bgColor="#EDE9FE"
            />
            <SummaryCard
              label="NF-e"
              value={resumo?.por_tipo?.nfe ?? 0}
              icon={FileText}
              iconColor={COLORS.NFe}
              bgColor="#DCFCE7"
            />
            <SummaryCard
              label="NFS-e"
              value={resumo?.por_tipo?.nfse ?? 0}
              icon={File}
              iconColor={COLORS.NFSe}
              bgColor="#EDE9FE"
            />
            <SummaryCard
              label="NFC-e"
              value={resumo?.por_tipo?.nfce ?? 0}
              icon={ShoppingCart}
              iconColor={COLORS.NFCe}
              bgColor="#FFF7ED"
            />
            <SummaryCard
              label="CT-e"
              value={resumo?.por_tipo?.cte ?? 0}
              icon={Package}
              iconColor={COLORS.CTe}
              bgColor="#DBEAFE"
            />
            <SummaryCard
              label="Saídas"
              value={resumo?.total_saidas ?? 0}
              icon={TrendUp}
              iconColor="#10B981"
              bgColor="#D1FAE5"
            />
          </SimpleGrid>
        )}

        {/* Gráfico de linha */}
        <FormBox>
          <Box p={{ base: 3, md: 6 }}>
            <Text fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }} mb={2}>
              Empresas com Notas Importadas por Dia
            </Text>
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" mb={4}>
              Clique em um ponto do gráfico para ver as empresas daquele dia
            </Text>

            {isFetchingPorDia ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" color="blue.500" />
              </Flex>
            ) : chartData.length === 0 ? (
              <Flex justify="center" py={8}>
                <Text color="gray.500">Nenhum dado disponível para este período</Text>
              </Flex>
            ) : (
              <Box h={{ base: '280px', md: '400px' }} mx={{ base: -2, md: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    onClick={handleChartClick}
                    margin={{ left: -10, right: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis
                      dataKey="dataFormatada"
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E2E8F0' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E2E8F0' }}
                      width={40}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={{ stroke: '#E2E8F0' }}
                      width={40}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="qt_empresas"
                      name="Empresas"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, fill: '#3B82F6', cursor: 'pointer' }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="qt_notas"
                      name="Notas"
                      stroke="#22C55E"
                      strokeWidth={2}
                      dot={{ fill: '#22C55E', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, fill: '#22C55E', cursor: 'pointer' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>
        </FormBox>

        {/* Empresas por tipo de arquivo */}
        <FormBox>
          <Box p={{ base: 3, md: 6 }} mt={6}>
            <Flex
              justify="space-between"
              align={{ base: 'flex-start', md: 'center' }}
              mb={4}
              flexDir={{ base: 'column', md: 'row' }}
              gap={2}
            >
              <Box>
                <Text fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }}>
                  Empresas por Tipo de Arquivo
                </Text>
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500">
                  Ordenado da maior para menor quantidade de notas
                </Text>
              </Box>
              {totalEmpresas > 0 && (
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                  {totalEmpresas} empresa(s) encontrada(s)
                </Text>
              )}
            </Flex>

            {/* Tabs de tipo de arquivo */}
            <Tabs
              variant="soft-rounded"
              colorScheme="blue"
              onChange={handleTabChange}
              index={['NFe', 'NFSe', 'NFCe', 'CTe'].indexOf(selectedTipoArquivo)}
              mb={4}
            >
              <TabList overflowX="auto" pb={2} flexWrap={{ base: 'nowrap', md: 'wrap' }}>
                <Tab
                  fontSize={{ base: 'xs', md: 'sm' }}
                  px={{ base: 2, md: 4 }}
                  py={2}
                  mr={2}
                  _selected={{ bg: COLORS.NFe, color: 'white' }}
                  flexShrink={0}
                >
                  <HStack spacing={1}>
                    <FileText size={14} />
                    <Text>NF-e</Text>
                  </HStack>
                </Tab>
                <Tab
                  fontSize={{ base: 'xs', md: 'sm' }}
                  px={{ base: 2, md: 4 }}
                  py={2}
                  mr={2}
                  _selected={{ bg: COLORS.NFSe, color: 'white' }}
                  flexShrink={0}
                >
                  <HStack spacing={1}>
                    <File size={14} />
                    <Text>NFS-e</Text>
                  </HStack>
                </Tab>
                <Tab
                  fontSize={{ base: 'xs', md: 'sm' }}
                  px={{ base: 2, md: 4 }}
                  py={2}
                  mr={2}
                  _selected={{ bg: COLORS.NFCe, color: 'white' }}
                  flexShrink={0}
                >
                  <HStack spacing={1}>
                    <ShoppingCart size={14} />
                    <Text>NFC-e</Text>
                  </HStack>
                </Tab>
                <Tab
                  fontSize={{ base: 'xs', md: 'sm' }}
                  px={{ base: 2, md: 4 }}
                  py={2}
                  _selected={{ bg: COLORS.CTe, color: 'white' }}
                  flexShrink={0}
                >
                  <HStack spacing={1}>
                    <Package size={14} />
                    <Text>CT-e</Text>
                  </HStack>
                </Tab>
              </TabList>
            </Tabs>

            {/* Conteúdo da lista */}
            {isLoadingEmpresas ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" color="blue.500" />
              </Flex>
            ) : allEmpresas.length > 0 ? (
              <Box
                ref={scrollContainerRef}
                maxH={{ base: '350px', md: '500px' }}
                overflowY="auto"
                overflowX="auto"
                css={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a1a1a1',
                  },
                }}
              >
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th px={{ base: 2, md: 4 }} fontSize={{ base: '10px', md: 'xs' }}>
                        Cód.
                      </Th>
                      <Th px={{ base: 2, md: 4 }} fontSize={{ base: '10px', md: 'xs' }}>
                        Empresa
                      </Th>
                      <Th
                        px={{ base: 2, md: 4 }}
                        fontSize={{ base: '10px', md: 'xs' }}
                        display={{ base: 'none', md: 'table-cell' }}
                      >
                        CNPJ
                      </Th>
                      <Th px={{ base: 2, md: 4 }} fontSize={{ base: '10px', md: 'xs' }} isNumeric>
                        Total
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {allEmpresas.map(empresa => {
                      const totalPorTipo =
                        selectedTipoArquivo === 'NFe'
                          ? empresa.total_nfe
                          : selectedTipoArquivo === 'NFSe'
                            ? empresa.total_nfse
                            : selectedTipoArquivo === 'NFCe'
                              ? empresa.total_nfce
                              : empresa.total_cte;

                      return (
                        <Tr key={empresa.empresa_id} _hover={{ bg: 'gray.50' }}>
                          <Td px={{ base: 2, md: 4 }}>
                            <Text
                              fontSize={{ base: 'xs', md: 'sm' }}
                              color="gray.700"
                              fontWeight="medium"
                            >
                              {empresa.external_id || '-'}
                            </Text>
                          </Td>
                          <Td px={{ base: 2, md: 4 }}>
                            <Text
                              fontWeight="medium"
                              noOfLines={1}
                              maxW={{ base: '150px', md: '300px' }}
                              fontSize={{ base: 'xs', md: 'sm' }}
                            >
                              {empresa.razao_social || empresa.nome || '-'}
                            </Text>
                          </Td>
                          <Td px={{ base: 2, md: 4 }} display={{ base: 'none', md: 'table-cell' }}>
                            <Text fontSize="sm" color="gray.600">
                              {formatCNPJ(empresa.cnpj || '')}
                            </Text>
                          </Td>
                          <Td px={{ base: 2, md: 4 }} isNumeric>
                            <Text
                              fontWeight="bold"
                              color={COLORS[selectedTipoArquivo]}
                              fontSize={{ base: 'xs', md: 'sm' }}
                            >
                              {totalPorTipo.toLocaleString('pt-BR')}
                            </Text>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>

                {/* Indicador de carregamento no scroll */}
                {isFetchingNextPage && (
                  <Flex justify="center" py={4}>
                    <HStack spacing={2}>
                      <Spinner size="sm" color="blue.500" />
                      <Text fontSize="sm" color="gray.500">
                        Carregando mais...
                      </Text>
                    </HStack>
                  </Flex>
                )}

                {/* Indicador de fim da lista */}
                {!hasNextPage && allEmpresas.length > 0 && (
                  <Flex justify="center" py={4}>
                    <Text fontSize="sm" color="gray.400">
                      Fim da lista
                    </Text>
                  </Flex>
                )}
              </Box>
            ) : (
              <Flex justify="center" py={8}>
                <Text color="gray.500">
                  Nenhuma empresa com {selectedTipoArquivo} neste período
                </Text>
              </Flex>
            )}
          </Box>
        </FormBox>
      </Box>

      {/* Modal de empresas do dia */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: 'full', md: '4xl' }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          mx={{ base: 0, md: 4 }}
          my={{ base: 0, md: 4 }}
          borderRadius={{ base: 0, md: 'lg' }}
        >
          <ModalHeader px={{ base: 4, md: 6 }} py={{ base: 3, md: 4 }}>
            <HStack spacing={2}>
              <CalendarBlank size={20} color="#0077B6" />
              <Text fontSize={{ base: 'sm', md: 'lg' }} noOfLines={1}>
                Empresas em {selectedDate ? moment(selectedDate).format('DD/MM/YYYY') : ''}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton size={{ base: 'sm', md: 'md' }} />
          <ModalBody pb={6} px={{ base: 3, md: 6 }}>
            {isFetchingEmpresasDia ? (
              <Flex justify="center" py={8}>
                <Spinner size="lg" color="blue.500" />
              </Flex>
            ) : empresasDoDia.length === 0 ? (
              <Flex justify="center" py={8}>
                <Text color="gray.500" fontSize={{ base: 'sm', md: 'md' }}>
                  Nenhuma empresa encontrada para este dia
                </Text>
              </Flex>
            ) : (
              <Box>
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" mb={4}>
                  {empresasDoDia.length} empresa(s) encontrada(s)
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
                  {empresasDoDia.map(empresa => (
                    <Card key={empresa.empresa_id} borderRadius="lg" shadow="sm">
                      <CardBody p={{ base: 3, md: 4 }}>
                        <Text
                          fontWeight="bold"
                          noOfLines={1}
                          mb={1}
                          fontSize={{ base: 'sm', md: 'md' }}
                        >
                          {empresa.razao_social || empresa.nome || 'Empresa sem nome'}
                        </Text>
                        <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" mb={3}>
                          {formatCNPJ(empresa.cnpj || '')}
                        </Text>
                        <HStack spacing={1} flexWrap="wrap" gap={1}>
                          {empresa.detalhes_por_tipo.map(detalhe => (
                            <Badge
                              key={detalhe.tipo}
                              colorScheme={
                                detalhe.tipo === 'NFe'
                                  ? 'green'
                                  : detalhe.tipo === 'NFSe'
                                    ? 'purple'
                                    : detalhe.tipo === 'NFCe'
                                      ? 'orange'
                                      : 'blue'
                              }
                              px={{ base: 1, md: 2 }}
                              py={0.5}
                              borderRadius="md"
                              fontSize={{ base: '10px', md: 'xs' }}
                            >
                              {detalhe.tipo}: {detalhe.qt_notas_total}
                            </Badge>
                          ))}
                        </HStack>
                        <Flex justify="flex-end" mt={2}>
                          <Text
                            fontWeight="bold"
                            color="blue.600"
                            fontSize={{ base: 'sm', md: 'md' }}
                          >
                            Total: {empresa.total_notas.toLocaleString('pt-BR')}
                          </Text>
                        </Flex>
                      </CardBody>
                    </Card>
                  ))}
                </SimpleGrid>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </PageBox>
  );
}
